import * as React from "react";
import ConfigService from "../services/ConfigService";

function PaymentIcons(props: {
	position: 'basket-summary' | 'payment-header',
	isTradeUser?: boolean
}) {
	const configService = new ConfigService();
	const [activeIndex, setActiveIndex] = React.useState(0);
	const activeIndexRef = React.useRef(0);
	const [isMobile, setIsMobile] = React.useState(
		typeof window !== 'undefined' ? window.matchMedia('(max-width: 620px)').matches : false
	);
	const [shouldCycle, setShouldCycle] = React.useState(false);
	const [checkVersion, setCheckVersion] = React.useState(0);
	const containerRef = React.useRef<HTMLUListElement>(null);

	React.useEffect(() => {
		const mediaQuery = window.matchMedia('(max-width: 620px)');
		const handler = (e: MediaQueryListEvent) => setIsMobile(e.matches);
		mediaQuery.addEventListener('change', handler);
		return () => mediaQuery.removeEventListener('change', handler);
	}, []);

	React.useEffect(() => {
		let timeout: ReturnType<typeof setTimeout>;
		const handler = () => {
			clearTimeout(timeout);
			timeout = setTimeout(() => {
				setShouldCycle(false);
				setCheckVersion(version => version + 1);
			}, 150);
		};
		window.addEventListener('resize', handler);
		return () => {
			window.removeEventListener('resize', handler);
			clearTimeout(timeout);
		};
	}, []);

	const isCycling = props.position === 'payment-header' && shouldCycle;

	const cyclingIcons: React.ReactNode[] = [
		configService.imageUrls.visaIconUrl && (
			<img src={configService.imageUrls.visaIconUrl} className="c-payment-methods__logo" alt="Visa" data-test="payment-method-icon-visa" loading="lazy" width="31" height="20" />
		),
		configService.imageUrls.mastercardIconUrl && (
			<img src={configService.imageUrls.mastercardIconUrl} className="c-payment-methods__logo" alt="Mastercard" data-test="payment-method-icon-mastercard" loading="lazy" width="28" height="20" />
		),
		configService.imageUrls.americanExpressIconUrl && (
			<img src={configService.imageUrls.americanExpressIconUrl} className="c-payment-methods__logo" alt="American Express" data-test="payment-method-icon-american-express" loading="lazy" width="31" height="20" />
		),
		configService.imageUrls.googlePayIconUrl && (
			<img src={configService.imageUrls.googlePayIconUrl} className="c-payment-methods__logo" alt="Google Pay" data-test="payment-method-icon-google-pay" loading="lazy" width="50" height="20" />
		),
		configService.imageUrls.applePayIconUrl && (
			<img src={configService.imageUrls.applePayIconUrl} className="c-payment-methods__logo" alt="Apple Pay" data-test="payment-method-icon-apple-pay" loading="lazy" width="49" height="20" />
		),
		configService.imageUrls.paypalIconUrl && (
			<img src={configService.imageUrls.paypalIconUrl} className="c-payment-methods__logo" alt="PayPal" data-test="payment-method-icon-paypal" loading="lazy" width="79" height="20" />
		),
		configService.imageUrls.klarnaIconUrl && !props.isTradeUser && (
			<img src={configService.imageUrls.klarnaIconUrl} className="c-payment-methods__logo" alt="Klarna" data-test="payment-method-icon-klarna" loading="lazy" width="57" height="25" />
		),
		configService.imageUrls.clearpayIconUrl && !props.isTradeUser && (
			<img src={configService.imageUrls.clearpayIconUrl} className="c-payment-methods__logo" alt="Clearpay" data-test="payment-method-icon-afterpayclearpay" loading="lazy" width="74" height="25" />
		),
	].filter(Boolean) as React.ReactNode[];

	React.useLayoutEffect(() => {
		if (props.position !== 'payment-header') {
			setShouldCycle(false);
			return;
		}
		if (isMobile) {
			setShouldCycle(true);
			return;
		}
		setShouldCycle(willDisplayOverMultipleLines());
	}, [isMobile, props.position, cyclingIcons.length, checkVersion]);

	function willDisplayOverMultipleLines() {
		if (!containerRef.current) return false;
		const items = containerRef.current.children;
		return items.length > 1 && (items[items.length - 1] as HTMLElement).offsetTop > (items[0] as HTMLElement).offsetTop;
	}

	React.useEffect(() => {
		if (cyclingIcons.length === 0) return;
		if (activeIndexRef.current >= cyclingIcons.length) {
			activeIndexRef.current = 0;
			setActiveIndex(0);
		}
	}, [cyclingIcons.length]);

	React.useEffect(() => {
		if (!isCycling || cyclingIcons.length <= 1) return;
		const iconCount = cyclingIcons.length;
		const interval = setInterval(() => {
			activeIndexRef.current = (activeIndexRef.current + 1) % iconCount;
			setActiveIndex(activeIndexRef.current);
		}, 3000);
		return () => clearInterval(interval);
	}, [isCycling, cyclingIcons.length]);

	if (isCycling && cyclingIcons.length > 0) {
		return (
			<ul className="c-payment-methods c-payment-methods--payment-header" data-test="payment-method-icons">
				<li className="c-payment-methods__item c-payment-methods__item--cycling">
					{cyclingIcons.map((icon, index) => (
						<span key={index} className={`c-payment-methods__cycling-icon${index === activeIndex ? ' c-payment-methods__cycling-icon--active' : ''}`}>
							{icon}
						</span>
					))}
				</li>
			</ul>
		);
	}

	return (
		<ul ref={containerRef} className={`c-payment-methods ${props.position === 'basket-summary' ? 'c-payment-methods--center' : ''} ${props.position === 'payment-header' ? 'c-payment-methods--payment-header' : ''}  ${configService.imageUrls.paymentMethodIconCount > 6 ? 'c-payment-methods--multiple-lines' : ''}`} data-test="payment-method-icons">
			{
				configService.imageUrls.visaIconUrl ?
					<li className="c-payment-methods__item">
						<img src={configService.imageUrls.visaIconUrl} className="c-payment-methods__logo" alt="Visa" data-test="payment-method-icon-visa" loading="lazy" width="31" height="20" />
					</li>
					: null}
			{
				configService.imageUrls.mastercardIconUrl ?
					<li className="c-payment-methods__item">
						<img src={configService.imageUrls.mastercardIconUrl} className="c-payment-methods__logo" alt="Mastercard" data-test="payment-method-icon-mastercard" loading="lazy" width="28" height="20" />
					</li>
					: null}
			{
				configService.imageUrls.americanExpressIconUrl ?
					<li className="c-payment-methods__item">
						<img src={configService.imageUrls.americanExpressIconUrl} className="c-payment-methods__logo" alt="American Express" data-test="payment-method-icon-american-express" loading="lazy" width="31" height="20" />
					</li>
					: null}
			{
				configService.imageUrls.googlePayIconUrl ?
					<li className="c-payment-methods__item">
						<img src={configService.imageUrls.googlePayIconUrl} className="c-payment-methods__logo" alt="Google Pay" data-test="payment-method-icon-google-pay" loading="lazy" width="50" height="20" />
					</li>
					: null}
			{
				configService.imageUrls.applePayIconUrl ?
					<li className="c-payment-methods__item">
						<img src={configService.imageUrls.applePayIconUrl} className="c-payment-methods__logo" alt="Apple Pay" data-test="payment-method-icon-apple-pay" loading="lazy" width="49" height="20" />
					</li>
					: null}
			{
				configService.imageUrls.paypalIconUrl ?
					<li className="c-payment-methods__item">
						<img src={configService.imageUrls.paypalIconUrl} className="c-payment-methods__logo" alt="PayPal" data-test="payment-method-icon-paypal" loading="lazy" width="79" height="20" />
					</li>
					: null}
			{
				configService.imageUrls.klarnaIconUrl && !props.isTradeUser ?
					<li className="c-payment-methods__item">
						<img src={configService.imageUrls.klarnaIconUrl} className="c-payment-methods__logo" alt="Klarna" data-test="payment-method-icon-klarna" loading="lazy" width="57" height="25" />
					</li>
					: null}
			{
				configService.imageUrls.clearpayIconUrl && !props.isTradeUser ?
					<li className="c-payment-methods__item">
						<img src={configService.imageUrls.clearpayIconUrl} className="c-payment-methods__logo" alt="Clearpay" data-test="payment-method-icon-afterpayclearpay" loading="lazy" width="74" height="25" />
					</li>
					: null}
		</ul>
	);
}

export default PaymentIcons;
